import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { AuthenticationHandlers } from "../types/AuthenticationHandlers"

type HandlerNames = keyof AuthenticationHandlers
type DisabledFlags = {
  [K in HandlerNames as `is${Capitalize<K>}Disabled`]?: boolean
}

interface UseAuthenticationActionsArgs
  extends AuthenticationHandlers,
    DisabledFlags {
  idPrefix?: string
}

/**
 * Hook that provides authentication actions (login and logout) as Action objects.
 * Based on useTeamMemberActions pattern, this hook transforms authentication handlers
 * into Action objects that can be used in menus, buttons, etc.
 *
 * @param {UseAuthenticationActionsArgs} args - Configuration object
 * @returns Object containing login and logout actions, plus actions array
 */
export function useAuthenticationActions({
  idPrefix = "authentication",
  login,
  logout,
  isLoginDisabled = false,
  isLogoutDisabled = false
}: UseAuthenticationActionsArgs) {
  const { t } = useTranslation()

  const loginAction = useMemo<Action>(
    () => ({
      id: `${idPrefix}__login`,
      "data-testid": "login-button",
      label: t("Authentication.SignIn"),
      onClick: login,
      disabled: isLoginDisabled,
      icon: IconName.LOGIN
    }),
    [idPrefix, t, login, isLoginDisabled]
  )

  const logoutAction = useMemo<Action>(
    () => ({
      id: `${idPrefix}__logout`,
      "data-testid": "logout-button",
      label: t("Authentication.SignOut"),
      onClick: logout,
      disabled: isLogoutDisabled,
      icon: IconName.LOGOUT
    }),
    [idPrefix, t, logout, isLogoutDisabled]
  )

  const actions = useMemo<Action[]>(
    () => [loginAction, logoutAction].filter(action => !!action.onClick),
    [loginAction, logoutAction]
  )

  return {
    actions,
    loginAction,
    logoutAction
  }
}
